/**
 * MapControls — toggle control group for map overlays. Each control is
 * a labelled pill with a small dot indicator that fills when active.
 * Presentational only — consumer owns the boolean values + handlers.
 *
 * The default-shaped layout is one row of pills; pass `orientation="vertical"`
 * for a stacked layout that fits inside a narrow side panel.
 *
 * Linear: MYD-94 (I3).
 */

import { cn } from 'lib/utils'

export interface MapToggleControl {
	key: string
	label: string
	active: boolean
	onToggle: () => void
}

export interface MapControlsProps {
	controls: MapToggleControl[]
	orientation?: 'horizontal' | 'vertical'
	title?: string
	className?: string
}

export function MapControls({
	controls,
	orientation = 'horizontal',
	title,
	className,
}: MapControlsProps) {
	if (controls.length === 0) return null
	return (
		<div
			className={cn(
				'rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm px-3 py-2',
				className,
			)}
		>
			{title && (
				<h5 className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
					{title}
				</h5>
			)}
			<div
				className={cn(
					orientation === 'vertical'
						? 'flex flex-col gap-1.5'
						: 'flex flex-wrap items-center gap-1.5',
				)}
			>
				{/*
				  * `key` is destructured out of the spread rather than left in it.
				  * `MapToggleControl` carries a `key` field, so `{...c}` re-passed
				  * it into JSX and React warned "A props object containing a key
				  * prop is being spread into JSX" (DT-026). React strips `key`
				  * before it reaches the component, so TogglePill never received
				  * it — but when a key arrives via spread React cannot see it for
				  * reconciliation, which on a list of map markers can reuse the
				  * wrong element for a record.
				  */}
				{controls.map(({ key, ...rest }) => (
					<TogglePill key={key} {...rest} />
				))}
			</div>
		</div>
	)
}

function TogglePill({ label, active, onToggle }: MapToggleControl) {
	return (
		<button
			type="button"
			onClick={onToggle}
			className={cn(
				'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors',
				active
					? 'bg-[#0080fe] text-white border-[#0080fe]'
					: 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
			)}
		>
			<span
				className={cn(
					'w-2 h-2 rounded-full',
					active ? 'bg-white' : 'bg-gray-300',
				)}
			/>
			{label}
		</button>
	)
}

export default MapControls
